import React, { useEffect, useState, lazy, Suspense } from 'react';
import { AuthProvider, CustomRoutes, DataProvider, Resource } from 'react-admin';
import { Admin } from 'react-admin';
import { theme } from './theme/theme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { generateQueryClient } from '@/components-library';
import { BrowserRouter, Route, useLocation } from 'react-router-dom';
import { CustomPaths } from './shared/constants/appRoutes';
import { Resources } from './shared/constants/resources';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import AppLayout from './layout/AppLayout';
import OnlyWithNoAuth from './auth/OnlyWithNoAuth';
import LoginPage from './auth/LoginPage';
import LogoutPage from '@/src/auth/LogoutPage';
import { FirebaseAuthProvider } from '@/src/auth/FirebaseAuthProvider';
import { authStatus, firebaseAuth } from '@/src/auth/FirebaseAuth';
import { FirebaseDataProvider } from './auth/FirebaseDataProvider';

// Lazy load heavy components to reduce initial bundle size
const SignupPage = lazy(() => import('./auth/SignupPage'));
const ConfirmEmailPage = lazy(() => import('@/src/auth/ConfirmEmailPage'));
const PasswordRecovery = lazy(() => import('@/src/auth/PasswordRecovery'));
const PasswordCreation = lazy(() => import('@/src/auth/PasswordCreation'));
const SsoAuth = lazy(() => import('@/src/auth/SsoAuth'));
const ClubEdit = lazy(() => import('@/src/pages/clubProfile/edit/ClubEdit'));
const BillingEdit = lazy(() => import('@/src/pages/billing/edit/BillingEdit').then(m => ({ default: m.BillingEdit })));
const TeamList = lazy(() => import('@/src/pages/teamManagement/list/TeamList').then(m => ({ default: m.TeamList })));
const AcademyList = lazy(() => import('@/src/pages/academy/list/AcademyList'));

// Loading fallback component
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
);

export const queryClient = generateQueryClient({}, process.env.NODE_ENV, {
  auth: 'Authorization',
});

export const APP_SETTINGS = 'COCLocalStorageAppSettings';
export const CURRENT_VERSION = 1;

function ScrollTopOnLocationChange() {
  let location = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location?.pathname]);

  return null;
}

const App = () => {
  useEffect(() => {
    const appSettingsJson = localStorage.getItem(APP_SETTINGS);
    if (!appSettingsJson) {
      localStorage.clear();
      localStorage.setItem(APP_SETTINGS, JSON.stringify({ version: CURRENT_VERSION }));
      return;
    }
    const appSettings = JSON.parse(appSettingsJson);
    if (appSettings.version !== CURRENT_VERSION) {
      localStorage.clear();
      localStorage.setItem(APP_SETTINGS, JSON.stringify({ version: CURRENT_VERSION }));
      return;
    }
  }, []);

  const [authStatusCopy, setAuthStatusCopy] = useState(authStatus);
  useEffect(() => {
    firebaseAuth.onAuthStateChanged(
      () => {
        setAuthStatusCopy('completed');
      },
      () => {
        setAuthStatusCopy('error');
      }
    );
  }, []);
  if (authStatusCopy !== 'completed') return null;

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      localeText={{
        fieldMonthPlaceholder: (params) => {
          if (params.format === 'MMMM' || params.format === 'LLLL') {
            return 'MM';
          }
          return params.contentType === 'letter' ? 'MMMM' : 'MM';
        },
      }}
    >
      <BrowserRouter>
        <CssBaseline />
        <ScrollTopOnLocationChange />
        <Admin
          title="Swish"
          layout={AppLayout}
          dataProvider={FirebaseDataProvider as DataProvider}
          dashboard={null}
          authProvider={FirebaseAuthProvider() as unknown as AuthProvider}
          loginPage={LoginPage}
          theme={theme}
          queryClient={queryClient}
        >
          <CustomRoutes noLayout>
            <Route
              path={CustomPaths.Signup}
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnlyWithNoAuth>
                    <SignupPage />
                  </OnlyWithNoAuth>
                </Suspense>
              }
            />
            <Route
              path={CustomPaths.ConfirmEmail}
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnlyWithNoAuth>
                    <ConfirmEmailPage />
                  </OnlyWithNoAuth>
                </Suspense>
              }
            />
            <Route
              path={CustomPaths.PasswordRecovery}
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnlyWithNoAuth>
                    <PasswordRecovery />
                  </OnlyWithNoAuth>
                </Suspense>
              }
            />
            <Route
              path={CustomPaths.PasswordCreation}
              element={
                <Suspense fallback={<PageLoader />}>
                  <OnlyWithNoAuth>
                    <PasswordCreation />
                  </OnlyWithNoAuth>
                </Suspense>
              }
            />
            <Route 
              path={CustomPaths.SsoAuth} 
              element={
                <Suspense fallback={<PageLoader />}>
                  <SsoAuth />
                </Suspense>
              } 
            />
            <Route path={CustomPaths.Logout} element={<LogoutPage />} />
          </CustomRoutes>
          <Resource
            options={{ label: 'Club profile' }}
            name={Resources.ClubProfile}
            list={(props) => (
              <Suspense fallback={<PageLoader />}>
                <ClubEdit {...props} />
              </Suspense>
            )}
          />
          <Resource
            options={{ label: 'Billing' }}
            name={Resources.Billing}
            list={(props) => (
              <Suspense fallback={<PageLoader />}>
                <BillingEdit {...props} />
              </Suspense>
            )}
          />
          <Resource
            options={{ label: 'Team management' }}
            name={Resources.Groups}
            list={(props) => (
              <Suspense fallback={<PageLoader />}>
                <TeamList {...props} />
              </Suspense>
            )}
            create={(props) => (
              <Suspense fallback={<PageLoader />}>
                <TeamList {...props} />
              </Suspense>
            )}
          />
          <Resource
            options={{ label: 'Swish academy' }}
            name={Resources.Academy}
            list={(props) => (
              <Suspense fallback={<PageLoader />}>
                <AcademyList {...props} />
              </Suspense>
            )}
          />
        </Admin>
      </BrowserRouter>
    </LocalizationProvider>
  );
};

export default App;
